import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  FileText, Download, Filter, User, Calendar, Activity,
  Eye, Edit, Trash2, Plus, LogIn, LogOut, Check
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// Get current date in IST timezone
const getISTDate = () => {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

// Get start of month in IST timezone
const getISTMonthStart = () => {
  const now = new Date();
  const istDate = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  istDate.setDate(1);
  return istDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

const activityIcons = {
  login: LogIn,
  logout: LogOut,
  create: Plus,
  update: Edit,
  delete: Trash2,
  view: Eye,
  export: Download,
  approve: Check
};

const activityColors = {
  login: 'bg-blue-100 text-blue-700',
  logout: 'bg-gray-100 text-gray-700',
  create: 'bg-green-100 text-green-700',
  update: 'bg-yellow-100 text-yellow-700',
  delete: 'bg-red-100 text-red-700',
  view: 'bg-purple-100 text-purple-700',
  export: 'bg-cyan-100 text-cyan-700',
  approve: 'bg-emerald-100 text-emerald-700'
};

function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: getISTMonthStart(),
    end: getISTDate()
  });
  const [moduleFilter, setModuleFilter] = useState('all');
  const [activityFilter, setActivityFilter] = useState('all');
  const [page, setPage] = useState(0);
  const limit = 50;

  useEffect(() => {
    fetchLogs();
    fetchSummary();
  }, [dateRange, moduleFilter, activityFilter, page]);

  const fetchLogs = async () => {
    try {
      let params = [`skip=${page * limit}`, `limit=${limit}`];
      params.push(`start_date=${dateRange.start}`);
      params.push(`end_date=${dateRange.end}`);
      if (moduleFilter !== 'all') params.push(`module=${moduleFilter}`);
      if (activityFilter !== 'all') params.push(`activity_type=${activityFilter}`);
      
      const res = await axios.get(`${API}/audit-logs?${params.join('&')}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (error) {
      toast.error('Failed to load audit logs');
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await axios.get(`${API}/audit-logs/summary?days=30`);
      setSummary(res.data);
    } catch (error) {
      console.error('Failed to load summary');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(
        `${API}/audit-logs/export?start_date=${dateRange.start}&end_date=${dateRange.end}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_logs_${dateRange.start}_${dateRange.end}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Audit logs exported');
    } catch (error) {
      toast.error('Failed to export. Only admins can export.');
    }
  };

  const dailyData = summary?.daily_activity?.map(d => ({
    date: d._id,
    count: d.count
  })) || [];

  const modules = [
    'Daily Operations', 'GST', 'Purchase Orders', 'Incidents', 'Accounts', 
    'Users', 'Settings', 'e_charging', 'lubricants', 'pump_profile', 
    'daily_stock', 'tt_readings', 'expenses', 'withdrawals', 'customer_payments',
    'customer_sales', 'subscription', 'Multi-Station', 'reports', 'customers'
  ];
  const activities = ['login', 'logout', 'create', 'update', 'delete', 'view', 'export', 'approve', 'sale', 'restock'];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="audit-logs-page">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold">Audit Logs</h1>
            <p className="text-muted text-sm">Track all user activities for compliance</p>
          </div>
          <Button onClick={handleExport} data-testid="export-audit-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Summary Stats */}
        {summary && (
          <div className="grid md:grid-cols-4 gap-4">
            <Card className="p-4 border border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Activity className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-muted">Total Activities (30d)</p>
                  <p className="text-xl font-bold">
                    {Object.values(summary.by_type || {}).reduce((a, b) => a + b, 0)}
                  </p>
                </div>
              </div>
            </Card>
            <Card className="p-4 border border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100">
                  <User className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-muted">Active Users</p>
                  <p className="text-xl font-bold">
                    {Object.keys(summary.by_user || {}).length}
                  </p>
                </div>
              </div>
            </Card>
            <Card className="p-4 border border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-muted">Modules Accessed</p>
                  <p className="text-xl font-bold">
                    {Object.keys(summary.by_module || {}).length}
                  </p>
                </div>
              </div>
            </Card>
            <Card className="p-4 border border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100">
                  <Calendar className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-muted">Days with Activity</p>
                  <p className="text-xl font-bold">
                    {dailyData.length}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Activity Chart */}
        {dailyData.length > 0 && (
          <Card className="p-6 border border-border">
            <h3 className="font-semibold mb-4">Daily Activity (Last 30 Days)</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                  fontSize={10}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(d) => new Date(d).toLocaleDateString()}
                  formatter={(v) => [v, 'Activities']}
                />
                <Bar dataKey="count" fill="#3B82F6" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Activity Breakdown */}
        {summary && (
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-4 border border-border">
              <h4 className="font-semibold mb-3">By User</h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {Object.entries(summary.by_user || {}).sort((a, b) => b[1] - a[1]).map(([user, count]) => (
                  <div key={user} className="flex justify-between items-center">
                    <span className="text-sm">{user}</span>
                    <span className="text-sm font-medium bg-secondary px-2 py-1 rounded">{count}</span>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="p-4 border border-border">
              <h4 className="font-semibold mb-3">By Activity Type</h4>
              <div className="space-y-2">
                {Object.entries(summary.by_type || {}).sort((a, b) => b[1] - a[1]).map(([type, count]) => {
                  const Icon = activityIcons[type] || Activity;
                  return (
                    <div key={type} className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-muted" />
                        <span className="text-sm capitalize">{type}</span>
                      </div>
                      <span className={`text-sm font-medium px-2 py-1 rounded ${activityColors[type] || 'bg-gray-100'}`}>
                        {count}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="p-4 border border-border">
          <div className="flex flex-wrap items-center gap-4">
            <Filter className="w-4 h-4 text-muted" />
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="w-36"
              />
              <span className="text-muted">to</span>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="w-36"
              />
            </div>
            <Select value={moduleFilter} onValueChange={setModuleFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Module" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modules</SelectItem>
                {modules.map(m => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={activityFilter} onValueChange={setActivityFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Activity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Activities</SelectItem>
                {activities.map(a => (
                  <SelectItem key={a} value={a} className="capitalize">{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-muted ml-auto">{total} records</span>
          </div>
        </Card>

        {/* Logs Table */}
        <Card className="border border-border overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-muted uppercase">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-muted uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-muted uppercase">Role</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-muted uppercase">Activity</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-muted uppercase">Module</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-muted uppercase">Action</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-muted uppercase">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {logs.map((log) => {
                const Icon = activityIcons[log.activity_type] || Activity;
                return (
                  <tr key={log.id} className="hover:bg-secondary/50">
                    <td className="px-4 py-3 text-sm">
                      {new Date(log.timestamp + 'Z').toLocaleString('en-IN', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: 'Asia/Kolkata'
                      })}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium">{log.user_name}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        log.user_role === 'admin' ? 'bg-amber-100 text-amber-700' :
                        log.user_role === 'manager' ? 'bg-blue-100 text-blue-700' :
                        log.user_role === 'standard' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {log.user_role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${activityColors[log.activity_type] || 'bg-gray-100'}`}>
                        <Icon className="w-3 h-3" />
                        {log.activity_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">{log.module}</td>
                    <td className="px-4 py-3 text-sm max-w-xs truncate">{log.action}</td>
                    <td className="px-4 py-3 text-xs text-muted max-w-xs truncate">
                      {log.details ? JSON.stringify(log.details) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {logs.length === 0 && (
            <div className="text-center py-12 text-muted">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No audit logs for the selected filters</p>
            </div>
          )}
        </Card>

        {/* Pagination */}
        {total > limit && (
          <div className="flex justify-center gap-2">
            <Button
              variant="outline"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <span className="flex items-center px-4 text-sm text-muted">
              Page {page + 1} of {Math.ceil(total / limit)}
            </span>
            <Button
              variant="outline"
              disabled={(page + 1) * limit >= total}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default AuditLogs;
